import React, { useMemo, useState } from "react";
import { useParams } from "react-router";
import { categoryToTitle } from "@sscs/report/constants";
import { Pagination as PaginationType, PolicyCategory } from "@sscs/types";
import {
  Card,
  DataTable,
  DataTableColumn,
  PageNotFound,
  TreeNode,
  TreeView,
} from "@sscs/ui";
import { getPageItems } from "@sscs/utils";
import { useAllowedTo, useAppSelector } from "Hooks";
import { useDerivedScanLevel, usePolicyFilter } from "../../hooks";
import {
  selectOverrides,
  selectRevertOverrides,
} from "../../store/policyConfigSlice";
import {
  Filter,
  PoliciesByCategory,
  PolicyConfig,
  PolicyId,
} from "../../types";
import { filterPolicies, groupPoliciesByCategory } from "../../utils";
import PolicyRow from "../PolicyRow/PolicyRow";
import TableToolbar from "../TableToolbar/TableToolbar";
import styles from "./policyTable.module.scss";

type PolicyTableProps = {
  policyConfig: PolicyConfig;
};

const PolicyTable = ({ policyConfig }: PolicyTableProps): React.JSX.Element => {
  // the group we care about in this component tree is the one in the URL, not the selectedGroup in the header drop down
  const { groupId } = useParams();

  const { status, isError, policies, permissions } = policyConfig;

  const overrides = useAppSelector(selectOverrides);
  const reverts = useAppSelector(selectRevertOverrides);
  const { derivedScanLevel } = useDerivedScanLevel();

  const canModifyPolicies = useAllowedTo({ permissions, groupId });

  const { filter, setFilter, resetFilters } = usePolicyFilter();

  const [selectedCategory, setSelectedCategory] = useState<PolicyCategory>();
  const [pagination, setPagination] = useState<PaginationType>({
    currentPage: 1,
    itemsPerPage: 10,
  });

  const policyIds = useMemo((): PolicyId[] => {
    return policies ? Object.values(policies).map((policy) => policy.id) : [];
  }, [policies]);

  // The rail lists ALL categories (not the filtered set), so categories never
  // appear/disappear as the user types a filter — only their counts change.
  const categories = useMemo(
    (): PolicyCategory[] =>
      Object.keys(
        groupPoliciesByCategory(Object.values(policies)),
      ) as PolicyCategory[],
    [policies],
  );

  const filteredPolicies = useMemo((): PoliciesByCategory => {
    // Filter only if filter is set
    if (filter.policyId || filter.showEdited || filter.showCustomized) {
      const filtered = filterPolicies({
        filter: filter,
        overrides: overrides,
        policies: Object.values(policies),
        reverts: reverts,
        scanLevel: derivedScanLevel,
      });

      return groupPoliciesByCategory(filtered);
    }

    return groupPoliciesByCategory(Object.values(policies));
  }, [policies, filter, derivedScanLevel, overrides, reverts]);

  const filteredCount = Object.values(filteredPolicies).reduce(
    (sum, categoryPolicies) => sum + categoryPolicies.length,
    0,
  );

  const activeCategory =
    selectedCategory && categories.includes(selectedCategory)
      ? selectedCategory
      : categories[0];

  const visiblePolicies = filteredPolicies[activeCategory] ?? [];

  const categoryNodes = useMemo(
    (): TreeNode[] =>
      categories.map((category) => ({
        id: category,
        label: categoryToTitle[category] ?? category,
        count: filteredPolicies[category]?.length ?? 0,
      })),
    [categories, filteredPolicies],
  );

  const handleCategoryChange = (categoryId: string) => {
    const category = categories.find((candidate) => candidate === categoryId);
    if (!category) return;

    setSelectedCategory(category);
    setPagination((current) => ({ ...current, currentPage: 1 }));
  };

  const handleFilterChange = (nextFilter: Filter) => {
    setFilter(nextFilter);
    setPagination((current) => ({ ...current, currentPage: 1 }));

    const nextFiltered = groupPoliciesByCategory(
      filterPolicies({
        filter: nextFilter,
        overrides: overrides,
        policies: Object.values(policies),
        reverts: reverts,
        scanLevel: derivedScanLevel,
      }),
    );

    if (!nextFiltered[activeCategory]?.length) {
      const firstWithMatches = categories.find(
        (category) => nextFiltered[category]?.length,
      );
      if (firstWithMatches) setSelectedCategory(firstWithMatches);
    }
  };

  const columns: DataTableColumn[] = [
    { id: "id", label: "ID", width: "110px" },
    {
      id: "description",
      label: "Description",
      width: "100%",
      minWidth: "260px",
    },
    { id: "enabled", label: "Enabled", align: "center", width: "90px" },
    {
      id: "ci-cd-status",
      label: "CI/CD status",
      width: "130px",
    },
    {
      id: "actions",
      label: "",
      align: "right",
      width: "64px",
      hidden: !canModifyPolicies,
    },
  ];

  if (isError)
    return (
      <PageNotFound
        title="We can't find that page"
        byline="Try selecting a page from the sidebar"
      />
    );

  return (
    <Card.Card>
      <Card.Title
        as="h2"
        details={[
          `${Object.values(policies).length} policies in ${categories.length} categories`,
        ]}
      >
        Policies
      </Card.Title>
      <Card.Content flush>
        <TableToolbar
          policies={policies}
          policyIds={policyIds}
          filter={filter}
          count={filteredCount}
          onFilterChange={handleFilterChange}
          onFilterReset={resetFilters}
          isDisabled={status.status !== "SUCCESS"}
          permissions={permissions}
        />
        <div className={styles.split}>
          {categories.length > 0 && (
            <div className={styles.rail}>
              <TreeView
                title="Categories"
                aria-label="Policy categories"
                nodes={categoryNodes}
                selected={activeCategory ?? null}
                onChange={handleCategoryChange}
              />
            </div>
          )}
          <DataTable
            chrome="plain"
            size="small"
            title={categoryToTitle[activeCategory] ?? activeCategory}
            titleDetails={[
              `${visiblePolicies.length} ${visiblePolicies.length === 1 ? "policy" : "policies"}`,
            ]}
            columns={columns}
            status={status}
            empty={{
              subject: "policies",
              filters: {
                active: Boolean(
                  filter.policyId || filter.showEdited || filter.showCustomized,
                ),
                onReset: resetFilters,
              },
            }}
            className={styles["policy-table"]}
            pagination={{
              itemCount: visiblePolicies.length,
              pagination,
              onChange: setPagination,
            }}
          >
            {getPageItems(
              visiblePolicies,
              pagination.currentPage,
              pagination.itemsPerPage,
            ).map((policy, index) => (
              <PolicyRow
                key={policy.id}
                policy={policy}
                permissions={permissions}
                groupId={groupId}
                className={index % 2 ? styles["even-row"] : styles["odd-row"]}
              />
            ))}
          </DataTable>
        </div>
      </Card.Content>
    </Card.Card>
  );
};

export default PolicyTable;
